import { useEffect, useRef, useState } from "react";
import type { PostgrestError } from "@supabase/supabase-js";

interface QueryState<T> {
  data: T | null;
  error: PostgrestError | Error | null;
  loading: boolean;
}

interface QueryResult<T> {
  data: T | null;
  error: PostgrestError | Error | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * Tiny query hook — runs ``fetcher`` once on mount + whenever ``deps`` change,
 * polls every ``intervalMs`` for refresh, and exposes a manual ``refetch``.
 *
 * Fetcher must return a Supabase ``{ data, error }`` shape OR a plain value.
 * Errors don't throw — they land in ``error`` so views can render an empty
 * state with the message instead of going blank.
 */
export function useSupabaseQuery<T>(
  fetcher: () => Promise<{ data: T | null; error: PostgrestError | null }>,
  deps: unknown[] = [],
  intervalMs?: number,
): QueryResult<T> {
  const [state, setState] = useState<QueryState<T>>({
    data: null, error: null, loading: true,
  });
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const tick = useRef(0);

  const run = () => {
    const myTick = ++tick.current;
    setState((s) => ({ ...s, loading: true }));
    fetcherRef.current()
      .then((r) => {
        if (myTick !== tick.current) return; // stale
        setState({ data: r.data, error: r.error, loading: false });
      })
      .catch((e) => {
        if (myTick !== tick.current) return;
        setState({ data: null, error: e, loading: false });
      });
  };

  useEffect(() => {
    run();
    if (intervalMs && intervalMs > 0) {
      const id = setInterval(run, intervalMs);
      return () => clearInterval(id);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, intervalMs]);

  return { ...state, refetch: run };
}
